import React, { useCallback, useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_ENDPOINTS, buildApiUrl } from '../../config/apiConfig';
import AdminHubPageFrame from '../../components/admin/AdminHubPageFrame';
import AdminSectionHeader from '../../components/admin/AdminSectionHeader';
import AdminMetricCard from '../../components/admin/AdminMetricCard';
import AdminStatusBadge from '../../components/admin/AdminStatusBadge';
import AdminTableToolbar from '../../components/admin/AdminTableToolbar';
import AdminDataTable from '../../components/admin/AdminDataTable';
import AdminJsonDetails from '../../components/admin/AdminJsonDetails';
import { adminPlanTableRows, enforcementModeBadgeTone, readinessBoolTone } from '../../admin/adminHubPresentation';
import { buildExportFilename, exportAdminTableAsCsv, exportToJsonFile } from '../../admin/adminExportUtils';

async function fetchAdminJson(path) {
  try {
    const res = await fetch(buildApiUrl(path), { credentials: 'include' });
    if (!res.ok) throw new Error('bad');
    return { ok: true, data: await res.json() };
  } catch {
    return { ok: false, data: null };
  }
}

function buildSafeSnapshot(results) {
  const [plan, enf, guest, firm, ai, asyncS, plat, evRecent, orch, shadow] = results;
  const planRows = adminPlanTableRows(plan.data?.snapshot);
  const activeLike = planRows.filter((r) => r.is_active === 'active').length;
  const policy = guest.data?.limits?.policy || {};
  const asyncSummary = asyncS.data?.summary != null ? asyncS.data.summary : asyncS.data;
  const env = plat.data?.envHints || {};
  const envKeys = Object.keys(env);
  const configured = envKeys.filter((k) => env[k]).length;

  const allowance = ai.data?.preview?.allowanceOnly || ai.data?.preview?.allowance;

  const events =
    evRecent.ok && evRecent.data && evRecent.data.success !== false && Array.isArray(evRecent.data.events)
      ? evRecent.data.events
      : [];
  const orchData = orch.ok && orch.data && orch.data.success !== false ? orch.data : null;

  return {
    generatedAt: new Date().toISOString(),
    sources_ok: {
      plan_catalog: plan.ok,
      enforcement_readiness: enf.ok,
      guest_policy: guest.ok,
      firm_entitlement_preview: firm.ok,
      ai_allowance_preview: ai.ok,
      async_summary: asyncS.ok,
      platform_readiness: plat.ok,
      commercial_events_recent: evRecent.ok,
      commercial_orchestration_health: orch.ok,
      shadow_readiness_bundle: shadow.ok,
    },
    enforcement_mode: enf.data?.runtime?.enforcement_mode ?? null,
    resolver_bridge_enabled: !!enf.data?.runtime?.bridgeEnabled,
    shadow_mismatch_count: enf.data?.shadowStats?.featureMismatchCount ?? null,
    active_plans_like_count: activeLike,
    plan_catalog_row_count: planRows.length,
    guest_document_upload_allowed: policy.document_upload_allowed === true,
    guest_firm_mode_allowed: policy.firm_mode_allowed === true,
    firm_preview_firm_mode_enabled: firm.data?.entitlement?.firm_mode_enabled ?? null,
    firm_preview_addon_required: firm.data?.entitlement?.addon_required ?? null,
    ai_plan_code: ai.data?.planCode ?? null,
    ai_limit_units: allowance?.limit != null ? Number(allowance.limit) : null,
    ai_remaining_units: allowance?.remaining != null ? Number(allowance.remaining) : null,
    ai_allowance_source: allowance?.source ?? null,
    ai_enforcement: allowance?.enforcement ?? ai.data?.preview?.enforcement ?? null,
    async_pending_count: asyncSummary?.pending_count ?? null,
    async_retry_heavy_count: asyncSummary?.retry_heavy_count ?? null,
    readiness_env_keys_total: envKeys.length,
    readiness_env_keys_configured: configured,
    commercial_office_sync_failed_24h: orchData?.office_subscription_sync_failed_24h ?? null,
    commercial_trial_events_24h: orchData?.trial_lifecycle_events_24h ?? null,
    commercial_ai_warnings_24h: orchData?.ai_usage_warnings_24h ?? null,
    commercial_firm_warnings_24h: orchData?.firm_mode_warnings_24h ?? null,
    commercial_events_enabled: orchData?.flags?.COMMERCIAL_EVENTS_ENABLED ?? null,
    commercial_sync_automation_mode: orchData?.flags?.COMMERCIAL_SYNC_AUTOMATION_MODE ?? null,
    stale_process_sync_hint: orchData?.stale_process_sync_hint ?? null,
    commercial_recent_events_count: events.length,
    commercial_orchestration_json: orchData
      ? {
          window_hours: orchData.window_hours,
          office_subscription_sync_failed_24h: orchData.office_subscription_sync_failed_24h,
          trial_lifecycle_events_24h: orchData.trial_lifecycle_events_24h,
          ai_usage_warnings_24h: orchData.ai_usage_warnings_24h,
          firm_mode_warnings_24h: orchData.firm_mode_warnings_24h,
          last_office_subscription_sync_success_at: orchData.last_office_subscription_sync_success_at,
          stale_process_sync_hint: orchData.stale_process_sync_hint,
          flags: orchData.flags,
          process_last_subscription_sync_attempt: orchData.process_last_subscription_sync_attempt,
          last_failed_sync_event: orchData.last_failed_sync_event,
          counts_by_type_24h_top: Array.isArray(orchData.counts_by_type_24h)
            ? orchData.counts_by_type_24h.slice(0, 14)
            : [],
          orchestration_readiness_note: orchData.orchestration_readiness_note,
        }
      : null,
    commercial_events_sample: events.slice(0, 20).map((e) => ({
      event_type: e.event_type,
      office_id: e.office_id,
      ydim_user_id: e.ydim_user_id,
      event_status: e.event_status,
      occurred_at: e.occurred_at,
    })),
    shadow_readiness_ok: shadow.ok && shadow.data && shadow.data.success !== false,
    shadow_orchestration_mode: shadow.data?.flags?.ORCHESTRATION_SHADOW_MODE ?? null,
    shadow_stale_warning_level: shadow.data?.stale_governance?.warning_level ?? null,
    shadow_dlq_scenarios:
      shadow.data?.dlq_readiness?.omitted ? null : shadow.data?.dlq_readiness?.scenarios?.length ?? null,
    shadow_webhook_slots:
      shadow.data?.webhook_readiness?.omitted ? null : shadow.data?.webhook_readiness?.future_providers?.length ?? null,
    shadow_readiness_json:
      shadow.ok && shadow.data && shadow.data.success !== false
        ? {
            flags: shadow.data.flags,
            stale_governance: shadow.data.stale_governance,
            dlq_readiness: shadow.data.dlq_readiness,
            replay_recovery: shadow.data.replay_recovery,
            webhook_readiness: shadow.data.webhook_readiness,
            cross_project: shadow.data.cross_project
              ? {
                  spec_version: shadow.data.cross_project.spec_version,
                  dimensions: shadow.data.cross_project.dimensions,
                  services_count: Array.isArray(shadow.data.cross_project.services)
                    ? shadow.data.cross_project.services.length
                    : 0,
                }
              : null,
            safe_diagnostics_preview: shadow.data.safe_diagnostics_preview,
            audit: shadow.data.audit?.layers ? { layer_count: shadow.data.audit.layers.length } : shadow.data.audit,
            unified_orchestration: shadow.data.unified_orchestration?.omitted
              ? { omitted: true }
              : { has_template: !!shadow.data.unified_orchestration?.vocabulary },
            note: shadow.data.note,
          }
        : null,
  };
}

function flattenSnapshotForExport(snap) {
  const rows = [];
  Object.entries(snap).forEach(([k, v]) => {
    if (k === 'sources_ok' && v && typeof v === 'object') {
      Object.entries(v).forEach(([sk, sv]) => {
        rows.push({ __rowKey: `src-${sk}`, key: `sources_ok.${sk}`, value: String(!!sv) });
      });
    } else if (typeof v !== 'object' || v === null) {
      rows.push({
        __rowKey: k,
        key: k,
        value: v === null || v === undefined ? '' : String(v),
      });
    }
  });
  return rows;
}

export default function AdminCommercialSnapshotPage() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);
  const [snap, setSnap] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(false);
    try {
      const firmQ = new URLSearchParams({ is_guest: 'true', has_firm_jwt: 'false' });
      const aiQ = new URLSearchParams({ plan_code: 'free' });
      const results = await Promise.all([
        fetchAdminJson(API_ENDPOINTS.ADMIN_COMMERCIAL_PLAN_CATALOG),
        fetchAdminJson(API_ENDPOINTS.ADMIN_COMMERCIAL_ENFORCEMENT_READINESS),
        fetchAdminJson(API_ENDPOINTS.ADMIN_COMMERCIAL_GUEST_POLICY),
        fetchAdminJson(`${API_ENDPOINTS.ADMIN_COMMERCIAL_FIRM_ENTITLEMENT_PREVIEW}?${firmQ}`),
        fetchAdminJson(`${API_ENDPOINTS.ADMIN_COMMERCIAL_AI_ALLOWANCE_PREVIEW}?${aiQ}`),
        fetchAdminJson(API_ENDPOINTS.ADMIN_OPERATIONAL_ASYNC_SUMMARY),
        fetchAdminJson(API_ENDPOINTS.ADMIN_PLATFORM_READINESS),
        fetchAdminJson(`${API_ENDPOINTS.ADMIN_COMMERCIAL_EVENTS_RECENT}?limit=24`),
        fetchAdminJson(API_ENDPOINTS.ADMIN_COMMERCIAL_ORCHESTRATION_HEALTH),
        fetchAdminJson(API_ENDPOINTS.ADMIN_READINESS_SHADOW_INFRASTRUCTURE),
      ]);
      setSnap(buildSafeSnapshot(results));
    } catch {
      setErr(true);
      setSnap(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const crumbs = useMemo(
    () => [
      { label: t('adminHub.navCommercial'), to: '/admin/commercial/plans' },
      { label: t('adminHub.navCommercialSnapshot') },
    ],
    [t]
  );

  const displayCols = useMemo(
    () => [
      { key: 'metric', header: t('adminSnapshot.colMetricKey') },
      { key: 'value', header: t('adminKit.colValue') },
    ],
    [t]
  );

  const displayRows = useMemo(() => {
    if (!snap) return [];
    return Object.entries(snap)
      .filter(([k, v]) => k !== 'sources_ok' && (typeof v !== 'object' || v === null))
      .map(([key, value]) => ({
        __rowKey: key,
        metric: key,
        value: value === null || value === undefined ? '—' : String(value),
      }));
  }, [snap]);

  const exportRows = useMemo(() => (snap ? flattenSnapshotForExport(snap) : []), [snap]);
  const exportKeys = ['key', 'value'];

  const mode = snap?.enforcement_mode || '—';
  const modeTone = enforcementModeBadgeTone(mode);

  return (
    <AdminHubPageFrame
      titleKey="adminHub.commercialSnapshotTitle"
      descKey="adminHub.commercialSnapshotDesc"
      breadcrumbItems={crumbs}
    >
      <section className="admin-section-card">
        <AdminSectionHeader title={t('adminSnapshot.actionsTitle')} subtitle={t('adminSnapshot.actionsSub')} />
        <div className="admin-hub-kit-toolbar__actions" style={{ marginBottom: '1rem', gap: '0.5rem', display: 'flex', flexWrap: 'wrap' }}>
          <button type="button" className="admin-hub-btn" onClick={load} disabled={loading}>
            {loading ? t('adminPage.loading') : t('adminHub.loadAction')}
          </button>
        </div>
        {err ? (
          <div className="admin-state admin-state--error" role="status">
            <p>{t('adminHub.loadError')}</p>
          </div>
        ) : null}
        {snap ? (
          <div className="admin-hub-kit-stack">
            <AdminSectionHeader title={t('adminSnapshot.summaryTitle')} subtitle={t('adminSnapshot.summarySub')} />
            <div className="admin-hub-kit-metric-grid">
              <AdminMetricCard
                label={t('adminKit.metricEnforcementMode')}
                value={<AdminStatusBadge tone={modeTone}>{String(mode)}</AdminStatusBadge>}
              />
              <AdminMetricCard label={t('adminSnapshot.cardActivePlans')} value={String(snap.active_plans_like_count ?? '—')} />
              <AdminMetricCard
                label={t('adminSnapshot.cardGuestUpload')}
                value={
                  <AdminStatusBadge tone={snap.guest_document_upload_allowed ? 'warn' : 'ok'}>
                    {snap.guest_document_upload_allowed ? t('adminKit.yes') : t('adminKit.no')}
                  </AdminStatusBadge>
                }
                hint={t('adminSnapshot.cardGuestUploadHint')}
              />
              <AdminMetricCard
                label={t('adminSnapshot.cardFirmPreview')}
                value={
                  snap.firm_preview_firm_mode_enabled == null ? '—' : snap.firm_preview_firm_mode_enabled ? t('adminKit.yes') : t('adminKit.no')
                }
              />
              <AdminMetricCard label={t('adminSnapshot.cardAiPlan')} value={snap.ai_plan_code || '—'} />
              <AdminMetricCard label={t('adminSnapshot.cardAsyncPending')} value={snap.async_pending_count ?? '—'} />
              <AdminMetricCard
                label={t('adminSnapshot.cardReadiness')}
                value={
                  <AdminStatusBadge
                    tone={readinessBoolTone(
                      snap.readiness_env_keys_total > 0 && snap.readiness_env_keys_configured === snap.readiness_env_keys_total
                    )}
                  >
                    {snap.readiness_env_keys_configured ?? 0}/{snap.readiness_env_keys_total ?? 0}
                  </AdminStatusBadge>
                }
              />
              <AdminMetricCard
                label={t('adminSnapshot.cardCommercialSyncFailed24h')}
                value={String(snap.commercial_office_sync_failed_24h ?? '—')}
              />
              <AdminMetricCard
                label={t('adminSnapshot.cardCommercialEventsRecent')}
                value={String(snap.commercial_recent_events_count ?? '—')}
              />
              <AdminMetricCard
                label={t('adminSnapshot.cardCommercialAutomation')}
                value={snap.commercial_sync_automation_mode != null ? String(snap.commercial_sync_automation_mode) : '—'}
              />
              <AdminMetricCard
                label={t('adminSnapshot.cardStaleSyncHint')}
                value={
                  <AdminStatusBadge tone={snap.stale_process_sync_hint ? 'warn' : 'ok'}>
                    {snap.stale_process_sync_hint ? t('adminKit.yes') : t('adminKit.no')}
                  </AdminStatusBadge>
                }
              />
              <AdminMetricCard
                label={t('adminSnapshot.cardShadowReadiness')}
                value={
                  <AdminStatusBadge tone={readinessBoolTone(!!snap.shadow_readiness_ok)}>
                    {snap.shadow_readiness_ok ? t('adminKit.yes') : t('adminKit.no')}
                  </AdminStatusBadge>
                }
              />
              <AdminMetricCard
                label={t('adminSnapshot.cardShadowStaleLevel')}
                value={snap.shadow_stale_warning_level != null ? String(snap.shadow_stale_warning_level) : '—'}
              />
              <AdminMetricCard label={t('adminSnapshot.cardShadowDlqScenarios')} value={String(snap.shadow_dlq_scenarios ?? '—')} />
              <AdminMetricCard label={t('adminSnapshot.cardShadowWebhookSlots')} value={String(snap.shadow_webhook_slots ?? '—')} />
            </div>

            <AdminTableToolbar
              onExportCsv={() => exportAdminTableAsCsv(exportRows, exportKeys, 'commercial-snapshot')}
              onExportJson={() => exportToJsonFile(snap, buildExportFilename('commercial-snapshot', 'json'))}
            />

            <AdminSectionHeader title={t('adminSnapshot.tableTitle')} level={3} />
            <AdminDataTable columns={displayCols} rows={displayRows} emptyMessage={t('adminKit.tableEmpty')} compact />

            <AdminJsonDetails title={t('adminSnapshot.sourcesJson')} data={snap.sources_ok} />
            {snap.commercial_orchestration_json ? (
              <AdminJsonDetails title={t('adminSnapshot.commercialOrchestrationJson')} data={snap.commercial_orchestration_json} />
            ) : null}
            {snap.commercial_events_sample && snap.commercial_events_sample.length ? (
              <AdminJsonDetails title={t('adminSnapshot.commercialEventsSample')} data={snap.commercial_events_sample} />
            ) : null}
            {snap.shadow_readiness_json ? (
              <AdminJsonDetails title={t('adminSnapshot.shadowReadinessJson')} data={snap.shadow_readiness_json} />
            ) : null}
          </div>
        ) : !loading && !err ? (
          <p className="admin-hub-kit-note">{t('adminSnapshot.promptLoad')}</p>
        ) : null}
      </section>
    </AdminHubPageFrame>
  );
}
